import { useEffect, useState } from 'react'
import userService, { ROLE_LABELS, ROLE_BADGE, ROLE_OPTIONS } from '../../services/userService'
import teamService from '../../services/teamService'
import { useAuthStore } from '../../store/authStore'
import { HiOutlinePlus, HiOutlineX, HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi'
import toast from 'react-hot-toast'

function UserManagement() {
  const { user: me } = useAuthStore()
  const [users, setUsers] = useState([])
  const [teams, setTeams] = useState([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null) // user object or 'new'

  const load = async () => {
    setLoading(true)
    try {
      const res = await userService.list()
      if (res.status === 'success') setUsers(res.data)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    teamService.list().then((r) => { if (r.status === 'success') setTeams(r.data) }).catch(() => {})
  }, [])

  const handleDelete = async (u) => {
    if (!window.confirm(`Xóa tài khoản "${u.username}"?`)) return
    try {
      await userService.remove(u.id)
      toast.success('Đã xóa tài khoản')
      load()
    } catch { /* interceptor */ }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="page-title">Phân quyền</h1>
          <p className="page-subtitle">Quản lý tài khoản, vai trò và nhóm nhân sự</p>
        </div>
        <button onClick={() => setEditing('new')} className="btn-primary">
          <HiOutlinePlus className="w-5 h-5" /> Thêm tài khoản
        </button>
      </div>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><div className="spinner w-8 h-8" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-secondary-500 border-b border-secondary-100 bg-secondary-50/50">
                  <th className="px-4 py-3 font-medium">Tài khoản</th>
                  <th className="px-4 py-3 font-medium">Liên hệ</th>
                  <th className="px-4 py-3 font-medium">Vai trò</th>
                  <th className="px-4 py-3 font-medium">Nhóm</th>
                  <th className="px-4 py-3 font-medium">Trạng thái</th>
                  <th className="px-4 py-3 font-medium text-right">Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-secondary-50 last:border-0 hover:bg-secondary-50/40">
                    <td className="px-4 py-3">
                      <div className="font-medium text-secondary-900">{u.full_name}</div>
                      <div className="text-2xs text-secondary-400">@{u.username}{u.id === me?.id && ' (bạn)'}</div>
                    </td>
                    <td className="px-4 py-3 text-secondary-600">
                      <div>{u.email || '—'}</div>
                      {u.phone && <div className="text-2xs text-secondary-400">{u.phone}</div>}
                    </td>
                    <td className="px-4 py-3"><span className={ROLE_BADGE[u.role]}>{ROLE_LABELS[u.role] || u.role}</span></td>
                    <td className="px-4 py-3 text-secondary-600">{u.team_name || <span className="text-secondary-300">—</span>}</td>
                    <td className="px-4 py-3">
                      {u.is_active ? <span className="badge-success">Hoạt động</span> : <span className="badge-neutral">Đã khóa</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {/* Chỉ Admin mới sửa/xoá được tài khoản Admin */}
                        {(u.role !== 'admin' || me?.role === 'admin') ? (
                          <>
                            <button onClick={() => setEditing(u)} className="p-2 text-secondary-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg" title="Sửa">
                              <HiOutlinePencil className="w-4 h-4" />
                            </button>
                            {u.id !== me?.id && (
                              <button onClick={() => handleDelete(u)} className="p-2 text-secondary-500 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Xóa">
                                <HiOutlineTrash className="w-4 h-4" />
                              </button>
                            )}
                          </>
                        ) : <span className="text-2xs text-secondary-300">— Admin —</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <UserFormModal
          user={editing === 'new' ? null : editing}
          teams={teams}
          canSetAdmin={me?.role === 'admin'}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load() }}
        />
      )}
    </div>
  )
}

function UserFormModal({ user, teams, canSetAdmin, onClose, onSaved }) {
  const isNew = !user
  const [form, setForm] = useState({
    username: user?.username || '',
    full_name: user?.full_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    role: user?.role || 'member',
    team_id: user?.team_id || '',
    is_active: user?.is_active ?? true,
    password: '',
  })
  const [saving, setSaving] = useState(false)
  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }))

  const save = async () => {
    if (isNew && (!form.username || !form.password || !form.full_name)) {
      toast.error('Nhập đủ tên đăng nhập, mật khẩu, họ tên')
      return
    }
    setSaving(true)
    try {
      const base = {
        full_name: form.full_name, email: form.email, phone: form.phone,
        role: form.role, is_active: form.is_active,
        team_id: form.team_id === '' ? null : Number(form.team_id),
      }
      if (isNew) {
        await userService.create({ ...base, username: form.username, password: form.password })
        toast.success('Đã tạo tài khoản')
      } else {
        if (form.password) base.password = form.password
        await userService.update(user.id, base)
        toast.success('Đã cập nhật')
      }
      onSaved()
    } catch { /* interceptor */ } finally { setSaving(false) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative card w-full max-w-md p-6 animate-scale-in">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-secondary-900">{isNew ? 'Thêm tài khoản' : 'Sửa tài khoản'}</h3>
          <button onClick={onClose} className="p-1.5 text-secondary-400 hover:text-secondary-700"><HiOutlineX className="w-5 h-5" /></button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="label">Tên đăng nhập <span className="text-red-500">*</span></label>
            <input className="input disabled:bg-secondary-50" value={form.username} disabled={!isNew}
              onChange={(e) => setField('username', e.target.value)} placeholder="vd: nhanvien01" />
          </div>
          <div>
            <label className="label">Họ tên <span className="text-red-500">*</span></label>
            <input className="input" value={form.full_name} onChange={(e) => setField('full_name', e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Email</label>
              <input className="input" value={form.email} onChange={(e) => setField('email', e.target.value)} />
            </div>
            <div>
              <label className="label">SĐT</label>
              <input className="input" value={form.phone} onChange={(e) => setField('phone', e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Vai trò</label>
              <select className="select" value={form.role} onChange={(e) => setField('role', e.target.value)}>
                {ROLE_OPTIONS.filter((r) => r !== 'admin' || canSetAdmin).map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Nhóm</label>
              <select className="select" value={form.team_id} onChange={(e) => setField('team_id', e.target.value)}>
                <option value="">— Không thuộc nhóm —</option>
                {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="label">Trạng thái</label>
            <select className="select" value={form.is_active ? '1' : '0'} onChange={(e) => setField('is_active', e.target.value === '1')}>
              <option value="1">Hoạt động</option>
              <option value="0">Khóa</option>
            </select>
          </div>
          <div>
            <label className="label">{isNew ? 'Mật khẩu' : 'Đổi mật khẩu (để trống nếu giữ nguyên)'} {isNew && <span className="text-red-500">*</span>}</label>
            <input type="password" className="input" value={form.password} onChange={(e) => setField('password', e.target.value)} placeholder="Tối thiểu 6 ký tự" />
          </div>

          <button onClick={save} disabled={saving} className="btn-primary w-full mt-2">
            {saving ? 'Đang lưu...' : (isNew ? 'Tạo tài khoản' : 'Lưu thay đổi')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default UserManagement
